/**
 * Unified Contract Clauses Hook
 * Provides consistent clause data for all user types (buyer, seller, agent)
 */

import {
  ClauseCategory,
  extractContractClausesWithIds,
} from '@/utils/clauseMapping';
import {
  ComprehensiveClauseCategory,
  extractComprehensiveContractClausesWithIds,
} from '@/utils/comprehensiveClauseMapping';
import { useUnifiedContract } from './useUnifiedContract';

/**
 * Unified contract clauses hook for all user types
 * This ensures all parties see identical clause data
 */
export const useUnifiedContractClauses = (contractId: string) => {
  const { data: contract } = useUnifiedContract(contractId);

  if (!contract) return [];

  // Use the enhanced clause mapping system with hierarchical IDs
  const clauseCategories = extractContractClausesWithIds(contract, contractId);

  // Convert to the old format for backward compatibility
  const contractClauses: { title: string; description: string }[] = [];

  clauseCategories.forEach((category) => {
    category.clauses.forEach((clause) => {
      contractClauses.push({
        title: clause.title,
        description: clause.description,
      });

      // Add sub-clauses if they exist
      if (clause.subClauses) {
        clause.subClauses.forEach((subClause) => {
          contractClauses.push({
            title: subClause.title,
            description: subClause.description,
          });
        });
      }
    });
  });

  return contractClauses;
};

/**
 * Enhanced contract clause categories hook for all user types
 * Returns the full clause category structure with hierarchical IDs
 */
export const useUnifiedContractClauseCategories = (
  contractId: string
): ClauseCategory[] => {
  const { data: contract } = useUnifiedContract(contractId);

  if (!contract) {
    return [];
  }

  return extractContractClausesWithIds(contract, contractId);
};

/**
 * Comprehensive contract clauses hook for all user types
 * Returns ALL contract specifics as negotiable clauses
 */
export const useComprehensiveContractClauses = (
  contractId: string
): ComprehensiveClauseCategory[] => {
  const { data: contract, isLoading, error } = useUnifiedContract(contractId);

  console.log('useComprehensiveContractClauses - contractId:', contractId);
  console.log('useComprehensiveContractClauses - contract:', contract);
  console.log('useComprehensiveContractClauses - isLoading:', isLoading);
  console.log('useComprehensiveContractClauses - error:', error);

  if (!contractId) {
    console.warn('useComprehensiveContractClauses - No contractId provided');
    return [];
  }

  if (!contract) {
    console.warn(
      'useComprehensiveContractClauses - No contract data available'
    );
    return [];
  }

  // Use the comprehensive clause mapping system
  const result = extractComprehensiveContractClausesWithIds(
    contract,
    contractId
  );
  console.log(
    'useComprehensiveContractClauses - extracted clauses result:',
    result
  );

  return result;
};
